import { env } from "@/env";
import {
  TranscriptionNoRealtimeProviderName,
  TranscriptionProviderName,
} from "@/hooks/transcriber/types";
import { getBooleanEnv } from "@/lib/utils";

export const ProductionConfig = {
  disablePremiumRestrictions: getBooleanEnv(
    "VITE_DISABLE_PREMIUM_RESTRICTIONS",
  ),
  // "Add recording" to an existing visit.
  enableMultirecording: getBooleanEnv("VITE_MULTIRECORDING_ENABLED"),
  // Visits, Templates and Settings in the visit workspace's style.
  enableAppRedesign: getBooleanEnv("VITE_APP_REDESIGN_ENABLED"),
  enableWhatsNew: getBooleanEnv("VITE_WHATS_NEW_ENABLED"),
  // Speaker roles in transcripts and diarization on by default.
  enableSpeakerRoles: getBooleanEnv("VITE_SPEAKER_ROLES_ENABLED"),
  // "Regenerate" on the main visit note (hidden for now; API unchanged).
  enableNoteRegenerate: getBooleanEnv("VITE_NOTE_REGENERATE_ENABLED"),
  // Mic button on every free-text field (note editor, templates, shortcuts,
  // visit title, feedback, profile). The assistant keeps its own mic.
  enableDictationEverywhere: getBooleanEnv("VITE_DICTATION_EVERYWHERE_ENABLED"),
  // English dictation on Deepgram nova-3-medical (as English live recording);
  // other languages stay on transcriptionsProvidersRealtime[0].
  enableDictationDeepgramEn: getBooleanEnv(
    "VITE_DICTATION_DEEPGRAM_EN_ENABLED",
  ),
  // Saved vocabulary (frequently used terms) in Settings.
  enableSavedVocabulary: getBooleanEnv("VITE_SAVED_VOCABULARY_ENABLED"),
  // "Add instruction" while recording, sent with the visit; the API applies
  // them and the spoken "insert <shortcut>" cue once the note is ready
  // (needs RECORDING_INSTRUCTIONS_ENABLED on the API).
  enableRecordingInstructions: getBooleanEnv(
    "VITE_RECORDING_INSTRUCTIONS_ENABLED",
  ),
  // The new-visit recorder in the v3.2 layout (top bar, live transcript,
  // floating pill). Off: the classic recorder.
  enableRecorderV32: getBooleanEnv("VITE_RECORDER_V32_ENABLED"),
  // Referral letters with templates (clinical lead, 28/10): Create → Referral
  // letter asks for the specialty, recipient, instructions and template;
  // Templates gets a "Referral letters" tab (own templates, made by hand or
  // from an example letter). Off: the one-click referral letter.
  enableReferralTemplates: getBooleanEnv("VITE_REFERRAL_TEMPLATES_ENABLED"),
  // "[verify]" values highlighted in the note, each opening a review card
  // (Accept / Edit) instead of the plain editor.
  enableReviewCard: getBooleanEnv("VITE_REVIEW_CARD_ENABLED"),
  // "Accept all" for "[verify]" values and "Copy anyway" / "Export anyway"
  // past unreviewed values (clinical lead, 28/10).
  enableReviewCopyAnyway: getBooleanEnv("VITE_REVIEW_COPY_ANYWAY_ENABLED"),
  // "Edit transcript" in the visit workspace, then "Update the note to
  // match" through the note assistant's fact-checked proposal.
  enableTranscriptEdit: getBooleanEnv("VITE_TRANSCRIPT_EDIT_ENABLED"),
  // v3.2 transcript drawer: timed segments (tap to play from there) and the
  // docked player with waveform, skip and speed.
  enableTranscriptPlayer: getBooleanEnv("VITE_TRANSCRIPT_PLAYER_ENABLED"),
  // "Sources": click a note sentence to see the transcript words it came
  // from, with "Replay this moment" when the recording has timings.
  enableNoteSources: getBooleanEnv("VITE_NOTE_SOURCES_ENABLED"),
  // AI flags on the transcript (red clinical risk, amber unclear audio, blue
  // auto-fixed), made by the worker (TRANSCRIPT_FLAGS_ENABLED).
  enableTranscriptFlags: getBooleanEnv("VITE_TRANSCRIPT_FLAGS_ENABLED"),
  // "Patient consented to recording" in the recorder; the note then starts
  // with a consent line (needs RECORDING_CONSENT_ENABLED on the API).
  enableRecordingConsent: getBooleanEnv("VITE_RECORDING_CONSENT_ENABLED"),
  // Assistant uploads attach at once, several at a time and Word documents
  // too; files are read only when the assistant or a new note uses them
  // (needs VISIT_SOURCE_FAST_ATTACH_ENABLED on the API).
  enableAssistantFastUploads: getBooleanEnv(
    "VITE_ASSISTANT_FAST_UPLOADS_ENABLED",
  ),
  // "Pre-chart" next to New visit: prepare the note before the visit with
  // the assistant, then "Start visit" records it and merges the two (needs
  // PRE_CHART_ENABLED on the API).
  enablePreChart: getBooleanEnv("VITE_PRE_CHART_ENABLED"),
  // "Fill a form" in the visit's Create menu: ROSC fills a PDF form from
  // the visit, the clinician reviews it and downloads the original PDF with
  // the values (needs FORM_COMPLETION_ENABLED and FORM_ENCRYPTION_KEY on the
  // API).
  enableFormCompletion: getBooleanEnv("VITE_FORM_COMPLETION_ENABLED"),
  // Guided first visit: the welcome sets one language for the app, the
  // consulta and the note, suggests templates from the specialty, offers a
  // practice consulta with a guided note, and keeps a "first steps"
  // checklist on Visits until it is done or dismissed.
  enableGuidedFirstVisit: getBooleanEnv("VITE_GUIDED_FIRST_VISIT_ENABLED"),
  // "Detect automatically" with Realtime off and for uploads. Needs the worker
  // that auto-detects "und" after Stop (#332); keep off where it is not
  // deployed.
  enableNonRealtimeAutoLanguage: getBooleanEnv(
    "VITE_NON_REALTIME_AUTO_LANGUAGE_ENABLED",
  ),
  enableAdvancedSettings: true,
  defaultTranscriptionProvider: TranscriptionNoRealtimeProviderName.OPENAI,
  transcriptionsProvidersRealtime: [TranscriptionProviderName.OPENAI],
  transcriptionsProvidersNoRealtime: [
    TranscriptionNoRealtimeProviderName.OPENAI,
  ],
} as const;

export const DevelopmentConfig = {
  disablePremiumRestrictions: getBooleanEnv(
    "VITE_DISABLE_PREMIUM_RESTRICTIONS",
  ),
  // "Add recording" to an existing visit.
  enableMultirecording: getBooleanEnv("VITE_MULTIRECORDING_ENABLED"),
  // Visits, Templates and Settings in the visit workspace's style.
  enableAppRedesign: getBooleanEnv("VITE_APP_REDESIGN_ENABLED"),
  enableWhatsNew: getBooleanEnv("VITE_WHATS_NEW_ENABLED"),
  // Speaker roles in transcripts and diarization on by default.
  enableSpeakerRoles: getBooleanEnv("VITE_SPEAKER_ROLES_ENABLED"),
  // "Regenerate" on the main visit note (hidden for now; API unchanged).
  enableNoteRegenerate: getBooleanEnv("VITE_NOTE_REGENERATE_ENABLED"),
  // Mic button on every free-text field (note editor, templates, shortcuts,
  // visit title, feedback, profile). The assistant keeps its own mic.
  enableDictationEverywhere: getBooleanEnv("VITE_DICTATION_EVERYWHERE_ENABLED"),
  // English dictation on Deepgram nova-3-medical (as English live recording);
  // other languages stay on transcriptionsProvidersRealtime[0].
  enableDictationDeepgramEn: getBooleanEnv(
    "VITE_DICTATION_DEEPGRAM_EN_ENABLED",
  ),
  // Saved vocabulary (frequently used terms) in Settings.
  enableSavedVocabulary: getBooleanEnv("VITE_SAVED_VOCABULARY_ENABLED"),
  // "Add instruction" while recording, sent with the visit; the API applies
  // them and the spoken "insert <shortcut>" cue once the note is ready
  // (needs RECORDING_INSTRUCTIONS_ENABLED on the API).
  enableRecordingInstructions: getBooleanEnv(
    "VITE_RECORDING_INSTRUCTIONS_ENABLED",
  ),
  // The new-visit recorder in the v3.2 layout (top bar, live transcript,
  // floating pill). Off: the classic recorder.
  enableRecorderV32: getBooleanEnv("VITE_RECORDER_V32_ENABLED"),
  // Referral letters with templates (clinical lead, 28/10): Create → Referral
  // letter asks for the specialty, recipient, instructions and template;
  // Templates gets a "Referral letters" tab (own templates, made by hand or
  // from an example letter). Off: the one-click referral letter.
  enableReferralTemplates: getBooleanEnv("VITE_REFERRAL_TEMPLATES_ENABLED"),
  // "[verify]" values highlighted in the note, each opening a review card
  // (Accept / Edit) instead of the plain editor.
  enableReviewCard: getBooleanEnv("VITE_REVIEW_CARD_ENABLED"),
  // "Accept all" for "[verify]" values and "Copy anyway" / "Export anyway"
  // past unreviewed values (clinical lead, 28/10).
  enableReviewCopyAnyway: getBooleanEnv("VITE_REVIEW_COPY_ANYWAY_ENABLED"),
  // "Edit transcript" in the visit workspace, then "Update the note to
  // match" through the note assistant's fact-checked proposal.
  enableTranscriptEdit: getBooleanEnv("VITE_TRANSCRIPT_EDIT_ENABLED"),
  // v3.2 transcript drawer: timed segments (tap to play from there) and the
  // docked player with waveform, skip and speed.
  enableTranscriptPlayer: getBooleanEnv("VITE_TRANSCRIPT_PLAYER_ENABLED"),
  // "Sources": click a note sentence to see the transcript words it came
  // from, with "Replay this moment" when the recording has timings.
  enableNoteSources: getBooleanEnv("VITE_NOTE_SOURCES_ENABLED"),
  // AI flags on the transcript (red clinical risk, amber unclear audio, blue
  // auto-fixed), made by the worker (TRANSCRIPT_FLAGS_ENABLED).
  enableTranscriptFlags: getBooleanEnv("VITE_TRANSCRIPT_FLAGS_ENABLED"),
  // "Patient consented to recording" in the recorder; the note then starts
  // with a consent line (needs RECORDING_CONSENT_ENABLED on the API).
  enableRecordingConsent: getBooleanEnv("VITE_RECORDING_CONSENT_ENABLED"),
  // Assistant uploads attach at once, several at a time and Word documents
  // too; files are read only when the assistant or a new note uses them
  // (needs VISIT_SOURCE_FAST_ATTACH_ENABLED on the API).
  enableAssistantFastUploads: getBooleanEnv(
    "VITE_ASSISTANT_FAST_UPLOADS_ENABLED",
  ),
  // "Pre-chart" next to New visit: prepare the note before the visit with
  // the assistant, then "Start visit" records it and merges the two (needs
  // PRE_CHART_ENABLED on the API).
  enablePreChart: getBooleanEnv("VITE_PRE_CHART_ENABLED"),
  // "Fill a form" in the visit's Create menu: ROSC fills a PDF form from
  // the visit, the clinician reviews it and downloads the original PDF with
  // the values (needs FORM_COMPLETION_ENABLED and FORM_ENCRYPTION_KEY on the
  // API).
  enableFormCompletion: getBooleanEnv("VITE_FORM_COMPLETION_ENABLED"),
  // Guided first visit: the welcome sets one language for the app, the
  // consulta and the note, suggests templates from the specialty, offers a
  // practice consulta with a guided note, and keeps a "first steps"
  // checklist on Visits until it is done or dismissed.
  enableGuidedFirstVisit: getBooleanEnv("VITE_GUIDED_FIRST_VISIT_ENABLED"),
  // "Detect automatically" with Realtime off and for uploads. Needs the worker
  // that auto-detects "und" after Stop (#332); keep off where it is not
  // deployed.
  enableNonRealtimeAutoLanguage: getBooleanEnv(
    "VITE_NON_REALTIME_AUTO_LANGUAGE_ENABLED",
  ),
  enableAdvancedSettings: true,
  defaultTranscriptionProvider:
    TranscriptionNoRealtimeProviderName.DEEPGRAM_NOVA_3_MEDICAL,
  transcriptionsProvidersRealtime: [
    TranscriptionProviderName.OPENAI,
    TranscriptionProviderName.SPEECHMATIC,
    TranscriptionProviderName.DEEPGRAM,
  ],
  transcriptionsProvidersNoRealtime: [
    TranscriptionNoRealtimeProviderName.DEEPGRAM_NOVA_3_MEDICAL,
  ],
} as const;

export const AppConfig =
  env.VITE_APP_ENVIRONMENT === "prod" ? ProductionConfig : DevelopmentConfig;
